import React, { useEffect, useMemo, useState } from 'react';
import { Columns, Save, Play, AlertTriangle, BookmarkPlus, MapPin, RotateCcw } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { toastService } from '../../../services/toastService';
import {
  migrationApi, type MigrationJob, type MigrationMapping, type MigrationField,
} from '../../../services/migrationApi';
import { Btn, Card, Banner, Chips, Modal, SOURCES, ANALYSABLE, toastApiError } from './shared';

/** The label a mapping uses for the file's single plain Stock/Qty column. */
export const SINGLE_STOCK = '*';
export const locationLabelText = (label: string) => (label === SINGLE_STOCK || label === '' ? 'Stock column' : label);

const FIELD_LABELS: Record<MigrationField, string> = {
  groupKey: 'Group key (handle / parent id)',
  name: 'Product name',
  variantName: 'Variant name',
  option1Name: 'Option 1 name',
  option1Value: 'Option 1 value',
  option2Name: 'Option 2 name',
  option2Value: 'Option 2 value',
  option3Name: 'Option 3 name',
  option3Value: 'Option 3 value',
  sku: 'SKU',
  barcode: 'Barcode',
  barcode2: 'Barcode 2',
  price: 'Price (all locations)',
  cost: 'Cost',
  category: 'Category',
  brand: 'Brand',
  vendor: 'Vendor',
  description: 'Description',
  imageUrl: 'Image URL',
  stock: 'Stock (single column)',
  reorderPoint: 'Reorder point',
  active: 'Active',
  ignore: 'Ignore this column',
};
const STOCK_AT = '__stock_at';
const PRICE_AT = '__price_at';

const COLUMN_OPTIONS = [
  ...(Object.keys(FIELD_LABELS) as MigrationField[]).map((f) => ({ value: f, label: FIELD_LABELS[f] })),
  { value: STOCK_AT, label: 'Stock at a location…', subtitle: 'one column per location' },
  { value: PRICE_AT, label: 'Price at a location…', subtitle: 'one column per location' },
];

type ColChoice = { choice: string; label?: string };

function choicesFrom(headers: string[], m?: MigrationMapping): Record<string, ColChoice> {
  const out: Record<string, ColChoice> = {};
  for (const h of headers) {
    if (m?.stockColumns && m.stockColumns[h] !== undefined) out[h] = { choice: STOCK_AT, label: m.stockColumns[h] };
    else if (m?.priceColumns && m.priceColumns[h] !== undefined) out[h] = { choice: PRICE_AT, label: m.priceColumns[h] };
    else out[h] = { choice: m?.columns?.[h] || 'ignore' };
  }
  return out;
}

/** Which profile the last analysis used — the tail of the 'analysed' event. */
function matchedHow(job: MigrationJob): string | null {
  const ev = [...(job.events || [])].reverse().find((e) => e.kind === 'analysed');
  if (!ev) return null;
  const i = ev.message.lastIndexOf(' — ');
  return i >= 0 ? ev.message.slice(i + 3) : null;
}

export const MappingStep: React.FC<{
  job: MigrationJob;
  locations: { locationId: string; name: string }[];
  productTypes: { productTypeId: string; name: string }[];
  onJob: (job: MigrationJob) => void;
  reload: () => void;
}> = ({ job, locations, productTypes, onJob, reload }) => {
  const headers = job.preview?.headers || [];
  const [cols, setCols] = useState<Record<string, ColChoice>>(() => choicesFrom(headers, job.mapping));
  const [locMap, setLocMap] = useState<Record<string, string>>(job.mapping?.locationMap || {});
  const [productTypeId, setProductTypeId] = useState<string>(job.mapping?.productTypeId || '');
  const [source, setSource] = useState<string>(job.mapping?.source || job.source || job.sourceGuess || 'other');
  const [busy, setBusy] = useState<'save' | 'reset' | 'analyse' | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  // A fresh analysis (new mapping / preview from the worker) resets the draft.
  const mappingSig = JSON.stringify([job.mapping, headers]);
  useEffect(() => {
    setCols(choicesFrom(headers, job.mapping));
    setLocMap(job.mapping?.locationMap || {});
    setProductTypeId(job.mapping?.productTypeId || '');
    setSource(job.mapping?.source || job.source || job.sourceGuess || 'other');
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingSig]);

  const draft: MigrationMapping = useMemo(() => {
    const columns: Record<string, MigrationField> = {};
    const stockColumns: Record<string, string> = {};
    const priceColumns: Record<string, string> = {};
    for (const h of headers) {
      const c = cols[h] || { choice: 'ignore' };
      if (c.choice === STOCK_AT) stockColumns[h] = (c.label ?? h).trim() || h;
      else if (c.choice === PRICE_AT) priceColumns[h] = (c.label ?? h).trim() || h;
      else columns[h] = c.choice as MigrationField;
    }
    return {
      source, columns, stockColumns, priceColumns, locationMap: locMap,
      ...(productTypeId ? { productTypeId } : {}),
      ...(job.mapping?.profileId ? { profileId: job.mapping.profileId } : {}),
    };
  }, [headers, cols, locMap, productTypeId, source, job.mapping?.profileId]);

  // Source location labels this mapping refers to — a plain Price column is the
  // default for every location; only a plain Stock column needs one.
  const labels = useMemo(() => {
    const s = new Set<string>([...Object.values(draft.stockColumns || {}), ...Object.values(draft.priceColumns || {})]);
    if (Object.values(draft.columns).includes('stock')) s.add(SINGLE_STOCK);
    return [...s];
  }, [draft]);
  const unmapped = labels.filter((l) => !locMap[l]);
  const canEdit = ANALYSABLE.includes(job.status);
  const how = matchedHow(job);
  const locOptions = locations.map((l) => ({ value: l.locationId, label: l.name || l.locationId }));
  const ptOptions = [{ value: '', label: 'No product type (default)' }, ...productTypes.map((p) => ({ value: p.productTypeId, label: p.name }))];

  const setCol = (h: string, choice: string) => {
    setCols((prev) => ({ ...prev, [h]: { choice, label: choice === STOCK_AT || choice === PRICE_AT ? (prev[h]?.label ?? h) : undefined } }));
    setDirty(true);
  };

  const saveAndAnalyse = async () => {
    setBusy('save');
    try {
      // Only send location entries for labels still in use.
      const locationMap = Object.fromEntries(labels.filter((l) => locMap[l]).map((l) => [l, locMap[l]]));
      const saved = await migrationApi.saveMapping(job.tenantId, job.jobId, { ...draft, locationMap });
      onJob(saved.job);
      const r = await migrationApi.analyse(job.tenantId, job.jobId);
      onJob(r.job);
      toastService.success('Mapping saved — analysing');
    } catch (e) {
      toastApiError(e, 'Could not save the mapping');
      reload();
    } finally {
      setBusy(null);
    }
  };

  /** Clear the column map so the next analysis re-detects the layout. */
  const resetToAuto = async () => {
    setBusy('reset');
    try {
      await migrationApi.saveMapping(job.tenantId, job.jobId, { source, columns: {}, stockColumns: {}, priceColumns: {}, locationMap: {} });
      const r = await migrationApi.analyse(job.tenantId, job.jobId);
      onJob(r.job);
      toastService.success('Re-detecting the layout');
    } catch (e) {
      toastApiError(e, 'Could not reset the mapping');
      reload();
    } finally {
      setBusy(null);
    }
  };

  const analyseNow = async () => {
    setBusy('analyse');
    try {
      const r = await migrationApi.analyse(job.tenantId, job.jobId);
      onJob(r.job);
    } catch (e) {
      toastApiError(e, 'Could not start the analysis');
    } finally {
      setBusy(null);
    }
  };

  if (!headers.length) {
    return (
      <Card title="Mapping">
        <div className="text-sm text-stone-500 space-y-3">
          <p>No preview yet — analyse the main file and RubyTill will recognise the export layout (or make a best guess you can correct here).</p>
          <Btn kind="black" icon={<Play size={16} />} busy={busy === 'analyse'} disabled={!canEdit || !(job.files || []).length || job.status === 'analysing'} onClick={analyseNow}>
            Analyse files
          </Btn>
          {!(job.files || []).length && <p className="text-xs text-stone-400">Upload a CSV or Excel export first.</p>}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card
        title={<span className="flex items-center gap-2"><Columns size={16} className="text-stone-500" />Column mapping</span>}
        actions={(
          <div className="flex items-center gap-2 flex-wrap">
            <Btn icon={<RotateCcw size={16} />} busy={busy === 'reset'} disabled={!canEdit || !!busy} onClick={resetToAuto} title="Forget this mapping and re-detect the layout">Re-detect</Btn>
            <Btn icon={<BookmarkPlus size={16} />} disabled={!job.mapping || dirty || !!busy} onClick={() => setProfileOpen(true)} title={dirty ? 'Save the mapping first' : undefined}>Save as profile</Btn>
            <Btn kind="black" icon={<Save size={16} />} busy={busy === 'save'} disabled={!canEdit || !!busy} onClick={saveAndAnalyse}>Save mapping &amp; analyse</Btn>
          </div>
        )}
      >
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-stone-500 mb-4">
          {job.sourceGuess && <span>Detected: <b className="text-stone-800">{SOURCES.find((s) => s.id === job.sourceGuess)?.label || job.sourceGuess}</b></span>}
          {how && <span className="flex items-center gap-1"><RubyGemMark size={14} />Mapping from: <b className="text-stone-800">{how}</b></span>}
          {dirty && <span className="text-amber-700 font-medium">Unsaved changes</span>}
        </div>
        {!canEdit && <div className="mb-3"><Banner tone="sky">The mapping is locked while the job is {job.status.replace(/_/g, ' ')}.</Banner></div>}

        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Source system</div>
          <Chips options={SOURCES} value={source} onChange={(v) => { setSource(v); setDirty(true); }} />
        </div>

        <div className="overflow-x-auto -mx-5 border-y border-stone-100">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="bg-stone-50 align-top">
                {headers.map((h) => {
                  const c = cols[h] || { choice: 'ignore' };
                  const perLoc = c.choice === STOCK_AT || c.choice === PRICE_AT;
                  return (
                    <th key={h} className="px-2 py-2 text-left font-normal min-w-[190px] border-r border-stone-100 last:border-r-0">
                      <div className="text-[11px] font-semibold text-stone-500 truncate mb-1" title={h}>{h}</div>
                      <SearchableDropdown
                        options={COLUMN_OPTIONS}
                        value={c.choice}
                        onChange={(v) => setCol(h, v || 'ignore')}
                        ariaLabel={`Field for column ${h}`}
                        searchPlaceholder="Find a field…"
                        disabled={!canEdit}
                      />
                      {perLoc && (
                        <input
                          value={c.label ?? h}
                          disabled={!canEdit}
                          onChange={(e) => { const label = e.target.value; setCols((p) => ({ ...p, [h]: { ...c, label } })); setDirty(true); }}
                          aria-label={`Location label for column ${h}`}
                          placeholder="Location label"
                          className="mt-1 w-full h-11 rounded-lg border border-stone-300 px-3 text-sm outline-none focus:border-[#DC143C]"
                        />
                      )}
                      {c.choice === 'ignore' && <div className="text-[11px] text-stone-400 mt-1">Not imported</div>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(job.preview?.rows || []).map((row, ri) => (
                <tr key={ri} className="border-t border-stone-100">
                  {headers.map((h, ci) => (
                    <td key={h} className={`px-3 py-2 max-w-[240px] truncate border-r border-stone-100 last:border-r-0 ${(cols[h]?.choice || 'ignore') === 'ignore' ? 'text-stone-300' : 'text-stone-700'}`} title={String(row[ci] ?? '')}>
                      {String(row[ci] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-400 mt-3">First {(job.preview?.rows || []).length} rows of {job.stats?.rows ?? 'the'} rows shown.</p>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title={<span className="flex items-center gap-2"><MapPin size={16} className="text-stone-500" />Locations</span>}>
          {(job.unmappedLocations || []).length > 0 && (
            <div className="mb-3">
              <Banner tone="amber" icon={<AlertTriangle size={16} />}>
                The last analysis could not place: {(job.unmappedLocations || []).map(locationLabelText).join(', ')}. Pick a location below, then save &amp; analyse.
              </Banner>
            </div>
          )}
          {labels.length === 0 ? (
            <p className="text-sm text-stone-500">No stock or per-location price columns are mapped. A plain Price column applies to every location.</p>
          ) : (
            <div className="space-y-3">
              {labels.map((l) => (
                <div key={l} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-3 items-center">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate">{locationLabelText(l)}</div>
                    <div className="text-xs text-stone-400">{l === SINGLE_STOCK ? 'the plain stock / qty column' : 'from the file'}</div>
                  </div>
                  <SearchableDropdown
                    options={locOptions}
                    value={locMap[l] || null}
                    onChange={(v) => { setLocMap((p) => ({ ...p, [l]: v || '' })); setDirty(true); }}
                    placeholder="Choose location…"
                    ariaLabel={`Location for ${locationLabelText(l)}`}
                    disabled={!canEdit}
                  />
                </div>
              ))}
              {unmapped.length > 0 && <p className="text-xs text-amber-700">{unmapped.length} still need a location — their stock will not import.</p>}
            </div>
          )}
        </Card>
        <Card title="Product type">
          <p className="text-sm text-stone-500 mb-3">Every imported product is created with this product type.</p>
          <SearchableDropdown
            options={ptOptions}
            value={productTypeId}
            onChange={(v) => { setProductTypeId(v || ''); setDirty(true); }}
            ariaLabel="Product type"
            disabled={!canEdit}
          />
        </Card>
      </div>

      {profileOpen && (
        <SaveProfileModal
          job={job}
          onClose={() => setProfileOpen(false)}
          onSaved={() => { setProfileOpen(false); reload(); }}
        />
      )}
    </div>
  );
};

const SaveProfileModal: React.FC<{ job: MigrationJob; onClose: () => void; onSaved: () => void }> = ({ job, onClose, onSaved }) => {
  const [name, setName] = useState(job.tenantName ? `${job.tenantName} export` : '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await migrationApi.saveProfile(job.tenantId, job.jobId, name.trim());
      toastService.success('Profile saved — this layout maps itself next time');
      onSaved();
    } catch (e) {
      toastApiError(e, 'Could not save the profile');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Save mapping as profile"
      icon={BookmarkPlus}
      onClose={onClose}
      busy={busy}
      footer={<><Btn onClick={onClose} disabled={busy}>Cancel</Btn><Btn kind="black" busy={busy} disabled={!name.trim()} onClick={save}>Save profile</Btn></>}
    >
      <p className="text-sm text-stone-500 mb-3">The saved mapping (as last analysed) is reused automatically when a file with the same columns arrives.</p>
      <label htmlFor="mig-profile" className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Profile name</label>
      <input
        id="mig-profile"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15"
      />
    </Modal>
  );
};

export default MappingStep;
